<template>
  <div class="completion-popup">
    <button class="completion-popup__close" @click="close">
      <span class="iconfont iclose-thin"></span>
    </button>

    <div class="completion-popup__content">
      <header class="completion-popup__header">
        <span class="completion-popup__celebration" aria-hidden="true">🎉</span>
        <h1 class="completion-popup__title">You finished the guide</h1>
        <div class="completion-popup__book">
          <BookCover
            class="completion-popup__book-cover"
            :doc="{
              title: data.book.title,
              author: data.book.author,
              cover_image: data.book.cover_image ?? undefined,
            }"
          />
          <div class="completion-popup__book-details">
            <h2 class="completion-popup__book-title">
              {{ data.book.title }}
            </h2>
            <p class="completion-popup__book-author">
              By {{ data.book.author }}
            </p>
          </div>
        </div>
      </header>

      <div class="completion-popup__sections">
        <CompletionAccordion
          v-if="data.takeaways.length"
          class="completion-popup__takeaways"
          title="💡 Key Takeaways"
          defaultOpen
        >
          <ul class="completion-popup__takeaway-list">
            <li
              v-for="(takeaway, index) in data.takeaways"
              :key="index"
              class="completion-popup__takeaway"
              v-html="renderInlineMarkdown(takeaway)"
            ></li>
          </ul>
        </CompletionAccordion>

        <CompletionAccordion
          v-if="data.highlights.count"
          class="completion-popup__highlights"
          title="📌 Highlights"
          :showDivider="false"
        >
          <template #badge>
            <span class="completion-popup__highlight-count">
              <span class="completion-popup__button-text">
                {{ data.highlights.count }}
              </span>
            </span>
          </template>
          <div class="completion-popup__highlight-list">
            <CompletionHighlight
              v-for="highlight in data.highlights.items"
              :key="highlight.id"
              :quote="highlight.quote"
              @open="openHighlight(highlight)"
            />
          </div>
        </CompletionAccordion>

        <section v-else class="completion-popup__missing-highlights">
          <div class="completion-popup__missing-highlights-header">
            <h2 class="completion-popup__missing-highlights-title">
              📌 Highlights
            </h2>
            <button
              class="completion-popup__add-highlight"
              type="button"
              @click="addHighlight"
            >
              <span class="completion-popup__button-text">Add highlight</span>
            </button>
          </div>
          <p class="completion-popup__highlight-nudge">
            Highlight key points to make them stick
          </p>
        </section>

        <CompletionAccordion
          v-if="data.exercises.length"
          class="completion-popup__exercises"
          title="✏️ Exercises"
        >
          <template #badge>
            <span class="completion-popup__exercise-count">
              <span class="completion-popup__button-text">
                {{ completedExerciseCount }}/{{ data.exercises.length }}
              </span>
            </span>
          </template>
          <ul class="completion-popup__exercise-list">
            <li
              v-for="exercise in data.exercises"
              :key="exercise.url_slug"
              class="completion-popup__exercise-item"
            >
              <button
                class="completion-popup__exercise"
                :class="{
                  'completion-popup__exercise--completed': exercise.completed,
                }"
                :disabled="exercise.completed"
                @click="openExercise(exercise)"
              >
                <span class="completion-popup__exercise-title">
                  <span>{{ exerciseTitle(exercise.title) }}</span>
                </span>
                <img
                  v-if="exercise.completed"
                  class="completion-popup__exercise-status"
                  src="@/assets.app/images/14335_checked.svg"
                  alt="Completed"
                />
                <span
                  v-else
                  class="completion-popup__exercise-status iconfont inext"
                  aria-hidden="true"
                ></span>
              </button>
            </li>
          </ul>
        </CompletionAccordion>
      </div>

      <CompletionRecommendations
        v-if="data.recommendations.length"
        :recommendations="data.recommendations"
        @select="openRecommendation"
        @favorite="trackRecommendationFavorite"
        @impression="trackRecommendationImpression"
      />
    </div>

    <div class="completion-popup__actions">
      <AppButton class="completion-popup__continue" big @click="continueFlow">
        Continue
      </AppButton>
      <CompletionMenu v-model="menuOpen" :data="data" @dismiss="dismiss" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

import { Mixpanel } from '@/analytics/mixpanel'
import type { CompletionFlowAction } from '@/analytics/mixpanel/mixpanel'
import type {
  CompletionExerciseItemData,
  CompletionHighlightItemData,
  CompletionPopupData,
  CompletionRecommendationData,
} from '@/models/completion'
import { emitter } from '@/services/mitt'

import AppButton from '@/app/components/button/AppButton.vue'
import BookCover from '@/app/components/book/card/BookCover.vue'
import CompletionAccordion from './CompletionAccordion.vue'
import CompletionHighlight from './CompletionHighlight.vue'
import CompletionMenu from './CompletionMenu.vue'
import CompletionRecommendations from './CompletionRecommendations.vue'

const props = defineProps<{
  data: CompletionPopupData
}>()

const emit = defineEmits<{
  (event: 'dismiss'): void
}>()

const router = useRouter()
const menuOpen = ref(false)
const completedExerciseCount = computed(
  () => props.data.exercises.filter((exercise) => exercise.completed).length,
)

onMounted(() => {
  document.addEventListener('keydown', onKeydown)
  emitter.on('closeOverlayMenu', closeTopLayer)
  emitter.emit('overlayMenuOpened')
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown)
  emitter.off('closeOverlayMenu', closeTopLayer)
  emitter.emit('overlayMenuClosed')
})

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    closeTopLayer()
  }
}

function closeTopLayer(): void {
  if (menuOpen.value) {
    menuOpen.value = false
  } else {
    close()
  }
}

function dismiss(): void {
  menuOpen.value = false
  emit('dismiss')
}

function trackAction(
  action: CompletionFlowAction,
  recommendation?: CompletionRecommendationData,
  position?: number,
): void {
  Mixpanel.trackCompletionFlowAction(
    props.data.book.id,
    action,
    recommendation && position
      ? {
          guideId: recommendation.book.id,
          category: recommendation.book.tags[0],
          position,
        }
      : undefined,
  )
}

function close(): void {
  trackAction('close')
  dismiss()
}

function continueFlow(): void {
  trackAction('continue')
  dismiss()
}

function openHighlight(highlight: CompletionHighlightItemData): void {
  trackAction('open-highlight')
  dismiss()
  void router.push({
    name: 'book.page',
    params: {
      url_slug: props.data.book.url_slug,
      page_url_slug: highlight.content.url_slug,
    },
    query: { highlight: highlight.id },
  })
}

function addHighlight(): void {
  trackAction('add-highlight')
  dismiss()
  void router.push({
    name: 'book.page',
    params: {
      url_slug: props.data.book.url_slug,
      page_url_slug: '',
    },
  })
}

function exerciseTitle(title: string): string {
  return title.replace(/^Exercise: /, '')
}

function openExercise(exercise: CompletionExerciseItemData): void {
  trackAction('open-exercise')
  dismiss()
  void router.push({
    name: 'book.page',
    params: {
      url_slug: props.data.book.url_slug,
      page_url_slug: exercise.url_slug,
    },
  })
}

function openRecommendation(
  recommendation: CompletionRecommendationData,
  position: number,
): void {
  trackAction('open-recommendation', recommendation, position)
  dismiss()
  void router.push({
    name: 'book.preview',
    params: { url_slug: recommendation.book.url_slug },
  })
}

function trackRecommendationFavorite(
  recommendation: CompletionRecommendationData,
  position: number,
  action: 'favorite-recommendation' | 'unfavorite-recommendation',
): void {
  trackAction(action, recommendation, position)
}

function trackRecommendationImpression(
  recommendation: CompletionRecommendationData,
  position: number,
): void {
  Mixpanel.trackCompletionFlowRecommendationShown(
    props.data.book.id,
    recommendation,
    position,
  )
}

/**
 * Escape a takeaway and render its bold and italic Markdown.
 *
 * Takeaways are short paragraphs where bold and italic text are useful, but
 * other Markdown elements do not belong in this part of the popup. We handle
 * those two constructs directly so this component does not need a full
 * Markdown dependency or accept unsupported Markdown elements.
 */
function renderInlineMarkdown(text: string): string {
  // The stored takeaway text is LLM generated and not fully trusted, but the
  // result of this call has to be rendered with v-html because we are
  // manually adding HTML tags, so we use the browser to first escape any
  // prior HTML tags by creating a temporary element and then reading back
  // escaped text from innerHTML.
  const element = document.createElement('span')
  element.textContent = text
  return element.innerHTML
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
}
</script>

<style lang="scss">
#app:has(.completion-popup) {
  overflow: hidden;
}
</style>

<style lang="scss" scoped>
.completion-popup {
  position: fixed;
  z-index: 11000;
  inset: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: $c-white;

  &__close {
    position: fixed;
    z-index: 1;
    top: env(safe-area-inset-top);
    right: env(safe-area-inset-right);
    display: grid;
    width: 56px;
    height: 56px;
    place-items: center;
    color: $c-black-6;
    font-size: 24px;

    @include hover {
      color: $c-black-2;
    }
  }

  &__content {
    display: flex;
    flex: 1 0 auto;
    flex-direction: column;
    gap: 36px;
    width: min(calc(100% - 40px), 552px);
    padding: max(40px, calc(env(safe-area-inset-top) + 18px)) 0
      calc(136px + env(safe-area-inset-bottom));
    margin-inline: auto;
  }

  &__actions {
    position: fixed;
    z-index: 1;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
    min-height: calc(100px + env(safe-area-inset-bottom));
    padding-bottom: env(safe-area-inset-bottom);
    background: linear-gradient(0deg, $c-white 0%, rgba($c-white, 0.8) 100%);
  }

  &__continue {
    width: 220px;
  }

  &__header {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__celebration {
    font-size: 28px;
    line-height: 1.3;
  }

  &__title {
    margin: 4px 0 16px;
    color: $c-black-2;
    font-size: 28px;
    font-weight: 500;
    line-height: 1.3;
    text-align: center;
  }

  &__book {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 188px;
    padding: 28px;
    gap: 20px;
    border-radius: 20px;
    background: $c-border;
  }

  &__book-cover {
    --cover-width: 100px;
    flex: 0 0 100px;
    width: 100px;
    overflow: hidden;
    border-radius: 10px;
    filter: drop-shadow(0 3px 10px rgba($c-black-1, 0.13));

    :deep(.book-cover__image),
    :deep(.fallback-cover) {
      width: 100%;
    }

    :deep(.book-cover__image) {
      object-fit: cover;
    }
  }

  &__book-details {
    flex: 0 1 159px;
    min-width: 0;
  }

  &__book-title {
    margin: 0;
    color: $c-black-15;
    font-size: 20px;
    font-weight: 500;
    line-height: 1.3;
    letter-spacing: -0.02em;
  }

  &__book-author {
    margin: 0;
    color: $c-black-3;
    font-size: 14px;
    line-height: 1.3;
  }

  &__sections {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
  }

  &__highlight-count {
    display: inline-flex;
    height: 24px;
    min-width: 32px;
    align-items: center;
    justify-content: center;
    padding: 0 12px;
    border-radius: 100px;
    margin-right: 12px;
    background: #ffc906;
    color: $c-black-15;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
  }

  &__highlight-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__missing-highlights {
    padding: 16px;
    border: 1px solid $c-black-13;
    border-radius: 14px;
    transition: border-color 0.15s;

    @include hover {
      border-color: $c-border-8;
    }
  }

  &__missing-highlights-header {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__missing-highlights-title {
    flex: 1;
    margin: 0;
    color: $c-black-2;
    font-size: 18px;
    font-weight: 400;
    line-height: 24px;
  }

  &__add-highlight {
    display: inline-flex;
    height: 32px;
    align-items: center;
    justify-content: center;
    padding: 8px 12px;
    border: 1px solid #ffc906;
    border-radius: 53px;
    background: #ffc906;
    color: $c-black-15;
    font-size: 12px;
    line-height: 14px;
  }

  &__button-text {
    display: inline-block;
    transform: translateY(1px);
  }

  &__highlight-nudge {
    padding: 12px 16px;
    border-radius: 8px;
    margin: 12px 0 0;
    background: rgba(#ffc906, 0.1);
    color: $c-black-15;
    font-size: 16px;
    line-height: 1.3;
  }

  &__exercises[open] {
    padding-bottom: 20px;
  }

  &__exercise-count {
    display: inline-flex;
    height: 24px;
    min-width: 43px;
    align-items: center;
    justify-content: center;
    padding: 0 12px;
    border-radius: 100px;
    margin-right: 12px;
    background: $c-border-7;
    color: $c-black-15;
    font-size: 12px;
    line-height: 16px;
  }

  &__exercise-list {
    padding: 0;
    margin: 0;
    list-style: none;
  }

  &__exercise-item + &__exercise-item {
    padding-top: 12px;
    border-top: 1px solid $c-border-7;
    margin-top: 12px;
  }

  &__exercise {
    display: flex;
    width: 100%;
    min-height: 24px;
    align-items: flex-start;
    gap: 12px;
    padding: 0;
    color: $c-black-15;
    font-size: 16px;
    line-height: 1.3;
    text-align: left;

    &--completed {
      color: $c-black-6;
      cursor: default;
      opacity: 1;
    }
  }

  &__exercise-title {
    display: flex;
    flex: 1;
    gap: 12px;
    padding-top: 2px;

    &::before {
      content: '•';
    }
  }

  &__exercise-status {
    display: grid;
    flex: 0 0 24px;
    width: 24px;
    height: 24px;
    place-items: center;
    color: $c-black-2;
    font-size: 16px;
  }

  &__takeaway-list {
    padding: 0;
    margin: 0;
    color: $c-black-3;
    font-size: 16px;
    line-height: 1.3;
  }

  &__takeaway {
    position: relative;
    padding-left: 20px;
    list-style: none;

    &::before {
      position: absolute;
      left: 0;
      content: '•';
    }

    :deep(strong) {
      display: block;
    }
  }

  &__takeaway + &__takeaway {
    margin-top: 12px;
  }

  @media (max-width: $w-mobile-s) {
    &__celebration {
      font-size: 24px;
    }

    &__title {
      margin: 4px 0 8px;
      font-size: 24px;
    }
  }
}

.night-mode .completion-popup {
  background: $c-dark-1;

  &__close,
  &__title,
  &__book-title,
  &__missing-highlights-title,
  &__exercise,
  &__exercise--completed,
  &__exercise-status,
  &__takeaway-list {
    color: $c-dark-4;
  }

  &__close {
    @include hover {
      color: $c-dark-4;
    }
  }

  &__actions {
    background: linear-gradient(0deg, $c-dark-1 0%, rgba($c-dark-1, 0.8) 100%);
  }

  &__book {
    background:
      linear-gradient(0deg, rgba(16, 16, 16, 0.8), rgba(16, 16, 16, 0.8)),
      linear-gradient(0deg, rgba(16, 16, 16, 0.4), rgba(16, 16, 16, 0.4)),
      $c-border;
  }

  &__book-author {
    color: $c-dark-6;
  }

  &__missing-highlights {
    border: 0;
    border-radius: 12px;
    background: $c-dark-2;
    transition: background-color 0.15s;

    @include hover {
      background: $c-dark-3;
    }
  }

  &__highlight-count {
    color: $c-dark-1;
  }

  &__add-highlight {
    color: $c-dark-1;
  }

  &__highlight-nudge {
    color: $c-dark-4;
  }

  &__exercise-count {
    background: $c-dark-5;
    color: $c-dark-4;
  }

  &__exercise-item + .completion-popup__exercise-item {
    border-color: $c-dark-3;
  }

  @media (max-width: $w-mobile-s) {
    &__actions {
      background: linear-gradient(
        180deg,
        rgba($c-dark-1, 0) 0%,
        $c-dark-1 100%
      );
    }
  }
}
</style>
